import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../../../api/client'
import type { OnboardingRecipientHistory } from '../../../api/onboardingTypes'
import { track } from '../../../analytics/events'
import RecipientNotice from './RecipientNotice'


export default function RecipientHistory({ email, history, onHistory, acknowledged, onAcknowledged, lookupEnabled = true }: {
  email: string
  lookupEnabled?: boolean
  history: OnboardingRecipientHistory | null
  onHistory: (history: OnboardingRecipientHistory | null) => void
  acknowledged: boolean
  onAcknowledged: (value: boolean) => void
}) {
  const { t } = useTranslation('pages')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!lookupEnabled) return
    let current = true
    setError(null)
    onHistory(null)
    const normalized = email.trim().toLowerCase()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized)) return
    const timer = setTimeout(() => {
      void api.onboardingRecipientHistory(normalized).then((result) => {
        if (current) onHistory(result)
      }).catch(() => {
        if (current) setError(t('adminOnboarding.history.historyUnavailable'))
      })
    }, 350)
    return () => { current = false; clearTimeout(timer) }
  }, [email, onHistory, t, lookupEnabled])

  if (error) return <p role="alert" className="text-sm text-destructive">{error}</p>
  if (!history || history.email !== email.trim().toLowerCase()) return <p className="text-xs text-muted-foreground">{t('adminOnboarding.history.enterEmail')}</p>
  const selected = history.selected
  return <section className="flex flex-col gap-2 text-sm" data-testid="onboarding-recipient-history">
    <RecipientNotice histories={[history]} />
    {history.classification === 'new' && !history.account_exists && <p>{t('adminOnboarding.history.newRecipient')}</p>}
    {selected && <>
      {history.classification === 'requires_acknowledgement' && <label className="mt-2 flex gap-2" data-testid="onboarding-reonboard-label">
        <input type="checkbox" data-testid="onboarding-allow-repeat" checked={acknowledged} onChange={(event) => {
          onAcknowledged(event.target.checked)
          track('admin_onboarding_reonboard_acknowledgement_changed', { onboarding_mode: 'single', onboarding_acknowledged: event.target.checked, count: 1 })
        }} />{t('adminOnboarding.history.ack')}
      </label>}
    </>}
    {!!history.history.length && <details className="mt-2" data-testid="onboarding-history-list"><summary className="cursor-pointer">{t('adminOnboarding.history.history', { count: history.history.length })}</summary>
      {history.history.map((entry) => <div key={entry.target_id} className="mt-2 border-t border-border pt-2">
        <p>{entry.created_at} · {entry.actor_email} · {entry.run_status} · {t(`adminOnboarding.history.${entry.complete ? 'complete' : 'unfinished'}`)} · {entry.run_id}</p>
        <ul>{entry.steps.map((step) => <li key={step.step}>{step.step}: {step.status}</li>)}</ul>
      </div>)}
    </details>}
  </section>
}
